/**
 * IndexedDB-backed store for encrypted thumbnail cache entries.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/AttachmentThumbnailStore.swift` with
 * IndexedDB replacing the per-file OPFS approach. Each (attachmentId, bucket)
 * pair is a distinct IndexedDB record, analogous to a separate file on iOS.
 *
 * # Security model
 * Stored bytes are AES-GCM ciphertext (nonce + tag included). The key bytes
 * never enter IndexedDB — they live only in memory during seal/open. Corrupted
 * entries are auto-evicted on open failure so a single bad row does not block
 * future writes to the same slot.
 *
 * # LRU eviction
 * Before each put, the store calls `shouldEvict` with the incoming byte
 * length. All eviction candidates are deleted in a single transaction before
 * the new entry is written, keeping the total under 200 KB.
 *
 * # Corrupted-entry auto-eviction
 * If `open()` throws `ThumbnailCryptoError` or `ThumbnailIntegrityError`,
 * the store deletes the corrupted row before re-throwing. This prevents a
 * stale bad blob from permanently blocking writes to that slot.
 *
 * # IndexedDB schema
 * Database name: `kaether_thumb_cache` (override via constructor for tests).
 * Object store: `entries` — keyed by composite `(attachmentId, bucket)`.
 * Record shape: `ThumbnailRecord` (ciphertext base64, metadata inline).
 *
 * @module AttachmentThumbnailStore
 */

import {
  type ThumbnailDimensionBucket,
  sealThumbnail,
  openThumbnail,
  deriveCacheKey,
  uint8ToBase64,
  base64ToUint8,
  ThumbnailCryptoError,
  ThumbnailIntegrityError,
} from "./AttachmentThumbnail";
import {
  shouldEvict,
  THUMB_CACHE_BYTE_LIMIT,
  type ThumbnailCacheEntry,
} from "./AttachmentThumbnailPolicy";

// ── Constants ─────────────────────────────────────────────────────────────────

export const THUMB_STORE_DB_NAME = "kaether_thumb_cache" as const;
export const THUMB_STORE_NAME = "entries" as const;
const DB_VERSION = 1;

// ── Error types ───────────────────────────────────────────────────────────────

export class ThumbnailStoreError extends Error {
  override readonly name = "ThumbnailStoreError";
  constructor(detail: string) {
    super(`Thumbnail store error: ${detail}`);
  }
}

// ── IndexedDB record shape ────────────────────────────────────────────────────

/**
 * What lives in IndexedDB. The ciphertext is stored as base64 so it
 * round-trips through JSON without loss.
 *
 * `idbKey` is the composite primary key string `${attachmentId}\x00${bucket}`
 * stored on the record so IndexedDB can use it as `keyPath`.
 */
interface ThumbnailRecord {
  readonly idbKey: string;
  readonly attachmentId: string;
  readonly bucket: ThumbnailDimensionBucket;
  /** Base64-encoded AES-GCM combined ciphertext (nonce | ct+tag). */
  readonly ciphertextB64: string;
  readonly byteLength: number;       // byte length of the ciphertext blob
  readonly lastAccessedAt: number;   // Unix ms
  readonly createdAt: number;        // Unix ms
}

/** Build the composite IDB primary key for a (attachmentId, bucket) pair. */
function idbKey(attachmentId: string, bucket: ThumbnailDimensionBucket): string {
  return `${attachmentId}\x00${bucket}`;
}

// ── Low-level IndexedDB helpers ───────────────────────────────────────────────

function openDB(dbName: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName, DB_VERSION);
    req.onupgradeneeded = (ev) => {
      const db = (ev.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(THUMB_STORE_NAME)) {
        db.createObjectStore(THUMB_STORE_NAME, { keyPath: "idbKey" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () =>
      reject(
        new ThumbnailStoreError(`IndexedDB open failed: ${req.error?.message}`),
      );
  });
}

function idbGet(
  db: IDBDatabase,
  key: string,
): Promise<ThumbnailRecord | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(THUMB_STORE_NAME, "readonly");
    const req = tx.objectStore(THUMB_STORE_NAME).get(key);
    req.onsuccess = () => resolve(req.result as ThumbnailRecord | undefined);
    req.onerror = () =>
      reject(
        new ThumbnailStoreError(`IndexedDB get failed: ${req.error?.message}`),
      );
  });
}

function idbPut(db: IDBDatabase, record: ThumbnailRecord): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(THUMB_STORE_NAME, "readwrite");
    const req = tx.objectStore(THUMB_STORE_NAME).put(record);
    req.onsuccess = () => resolve();
    req.onerror = () =>
      reject(
        new ThumbnailStoreError(`IndexedDB put failed: ${req.error?.message}`),
      );
  });
}

function idbDelete(db: IDBDatabase, key: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(THUMB_STORE_NAME, "readwrite");
    const req = tx.objectStore(THUMB_STORE_NAME).delete(key);
    req.onsuccess = () => resolve();
    req.onerror = () =>
      reject(
        new ThumbnailStoreError(
          `IndexedDB delete failed: ${req.error?.message}`,
        ),
      );
  });
}

function idbGetAll(db: IDBDatabase): Promise<ThumbnailRecord[]> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(THUMB_STORE_NAME, "readonly");
    const req = tx.objectStore(THUMB_STORE_NAME).getAll();
    req.onsuccess = () => resolve(req.result as ThumbnailRecord[]);
    req.onerror = () =>
      reject(
        new ThumbnailStoreError(
          `IndexedDB getAll failed: ${req.error?.message}`,
        ),
      );
  });
}

// ── AttachmentThumbnailStore ──────────────────────────────────────────────────

/**
 * IndexedDB-backed encrypted thumbnail cache.
 *
 * Consumer usage pattern:
 *
 * ```ts
 * const store = new AttachmentThumbnailStore();
 *
 * // On attachment download / thumbnail generation:
 * await store.put(thumbPixelBytes, "attach-xyz", "medium", attachmentKeyBytes);
 *
 * // On UI render:
 * const thumb = await store.get("attach-xyz", "medium", attachmentKeyBytes);
 * if (thumb != null) { /* use it *\/ }
 * ```
 *
 * The consumer is responsible for supplying the correct `attachmentKeyBytes`
 * (the same 32-byte root key used to seal the original attachment ciphertext).
 * The cache-key derivation (`deriveCacheKey`) is transparent to the consumer.
 */
export class AttachmentThumbnailStore {
  private readonly dbName: string;
  private db: IDBDatabase | null = null;

  constructor(dbName: string = THUMB_STORE_DB_NAME) {
    this.dbName = dbName;
  }

  // ── DB init ───────────────────────────────────────────────────────────────

  private async getDB(): Promise<IDBDatabase> {
    if (this.db == null) {
      this.db = await openDB(this.dbName);
    }
    return this.db;
  }

  // ── put ───────────────────────────────────────────────────────────────────

  /**
   * Encrypt `thumbBytes` and store it under `(attachmentId, bucket)`.
   *
   * Steps:
   *  1. Derive cache key from `attachmentKeyBytes` via HKDF.
   *  2. Seal with AES-GCM (AAD binds attachmentId + bucket).
   *  3. Apply LRU eviction if needed (200 KB cap).
   *  4. Write the new record.
   *
   * If an existing entry for the same (attachmentId, bucket) exists it is
   * overwritten.
   *
   * @throws `ThumbnailStoreError` on IndexedDB failures.
   * @throws `ThumbnailCryptoError` on seal failures (invalid key bytes).
   */
  async put(
    thumbBytes: Uint8Array,
    attachmentId: string,
    bucket: ThumbnailDimensionBucket,
    attachmentKeyBytes: Uint8Array,
  ): Promise<void> {
    const db = await this.getDB();
    const cacheKey = await deriveCacheKey(attachmentKeyBytes);
    const ciphertext = await sealThumbnail(thumbBytes, attachmentId, bucket, cacheKey);

    // LRU eviction: compute current entries excluding the slot we're about to write.
    const allRecords = await idbGetAll(db);
    const existingKey = idbKey(attachmentId, bucket);
    const currentEntries: ThumbnailCacheEntry[] = allRecords
      .filter((r) => r.idbKey !== existingKey)
      .map((r) => ({
        attachmentId: r.attachmentId,
        bucket: r.bucket,
        byteLength: r.byteLength,
        lastAccessedAt: r.lastAccessedAt,
      }));

    const toEvict = shouldEvict(
      currentEntries,
      ciphertext.byteLength,
      THUMB_CACHE_BYTE_LIMIT,
    );

    for (const key of toEvict) {
      await idbDelete(db, idbKey(key.attachmentId, key.bucket));
    }

    const now = Date.now();
    const record: ThumbnailRecord = {
      idbKey: existingKey,
      attachmentId,
      bucket,
      ciphertextB64: uint8ToBase64(ciphertext),
      byteLength: ciphertext.byteLength,
      lastAccessedAt: now,
      createdAt: now,
    };
    await idbPut(db, record);
  }

  // ── get ───────────────────────────────────────────────────────────────────

  /**
   * Retrieve and decrypt a cached thumbnail. Returns `null` if no entry
   * exists for `(attachmentId, bucket)`.
   *
   * On open failure (wrong key, tampered ciphertext, SHA-256 mismatch), the
   * corrupted row is deleted from IndexedDB before re-throwing, so the same
   * bad entry cannot block future writes.
   *
   * Updates `lastAccessedAt` on successful reads (LRU timestamp refresh).
   *
   * @throws `ThumbnailCryptoError` / `ThumbnailIntegrityError` on decrypt failure
   *         (after auto-evicting the corrupted record).
   */
  async get(
    attachmentId: string,
    bucket: ThumbnailDimensionBucket,
    attachmentKeyBytes: Uint8Array,
  ): Promise<Uint8Array | null> {
    const db = await this.getDB();
    const key = idbKey(attachmentId, bucket);
    const record = await idbGet(db, key);
    if (record == null) return null;

    const cacheKey = await deriveCacheKey(attachmentKeyBytes);
    const ciphertext = base64ToUint8(record.ciphertextB64);

    let thumbBytes: Uint8Array;
    try {
      thumbBytes = await openThumbnail(ciphertext, attachmentId, bucket, cacheKey);
    } catch (err) {
      // Auto-evict corrupted entry so it does not permanently block the slot.
      await idbDelete(db, key).catch(() => {/* best effort */});
      throw err;
    }

    // Refresh LRU timestamp.
    const updated: ThumbnailRecord = {
      ...record,
      lastAccessedAt: Date.now(),
    };
    await idbPut(db, updated).catch(() => {/* non-fatal: timestamp is best-effort */});

    return thumbBytes;
  }

  // ── has ───────────────────────────────────────────────────────────────────

  /**
   * Returns `true` if a ciphertext record exists for `(attachmentId, bucket)`.
   * Does not attempt decryption.
   */
  async has(
    attachmentId: string,
    bucket: ThumbnailDimensionBucket,
  ): Promise<boolean> {
    const db = await this.getDB();
    const record = await idbGet(db, idbKey(attachmentId, bucket));
    return record != null;
  }

  // ── evict ─────────────────────────────────────────────────────────────────

  /**
   * Explicitly remove a specific `(attachmentId, bucket)` entry.
   * No-op if the entry does not exist.
   */
  async evict(
    attachmentId: string,
    bucket: ThumbnailDimensionBucket,
  ): Promise<void> {
    const db = await this.getDB();
    await idbDelete(db, idbKey(attachmentId, bucket));
  }

  // ── evictAll ──────────────────────────────────────────────────────────────

  /**
   * Remove all cached entries for a given `attachmentId` across all buckets.
   * Useful when an attachment is deleted or message is wiped.
   */
  async evictAll(attachmentId: string): Promise<void> {
    const db = await this.getDB();
    const all = await idbGetAll(db);
    for (const r of all) {
      if (r.attachmentId === attachmentId) {
        await idbDelete(db, r.idbKey);
      }
    }
  }

  // ── totalBytes ────────────────────────────────────────────────────────────

  /**
   * Return the total byte size of all stored ciphertext blobs.
   */
  async totalBytes(): Promise<number> {
    const db = await this.getDB();
    const all = await idbGetAll(db);
    return all.reduce((s, r) => s + r.byteLength, 0);
  }

  // ── allEntries ────────────────────────────────────────────────────────────

  /**
   * Return metadata for all stored entries. Used by tests and diagnostics.
   * Does not decrypt.
   */
  async allEntries(): Promise<
    Array<{
      attachmentId: string;
      bucket: ThumbnailDimensionBucket;
      byteLength: number;
      lastAccessedAt: number;
    }>
  > {
    const db = await this.getDB();
    const all = await idbGetAll(db);
    return all.map((r) => ({
      attachmentId: r.attachmentId,
      bucket: r.bucket,
      byteLength: r.byteLength,
      lastAccessedAt: r.lastAccessedAt,
    }));
  }
}
